﻿<template>
  <div class="kanban-board">
    <loading-placeholder v-if="isLoading" :lines="20" class="kanban-board__loading" />
    <template v-else>
      <div v-if="isMobile" class="kanban-board__column-indicators">
        <span
          v-for="(kanbanColumn, index) in kanbanColumns"
          :key="'dot-' + kanbanColumn.status.id"
          :class="{'active': index === activeColumnIndex}"
          class="kanban-board__column-indicators__dot"
        />
      </div>
      <div
        ref="columnsContainer"
        :class="{'dragging' : isDragging, 'mobile': isMobile}"
        :style="columnsStyle"
        class="kanban-board__columns marker--kanban-board--columns"
      >
        <div
          v-for="(kanbanColumn, index) in kanbanColumns"
          :key="kanbanColumn.status.id"
          :class="[{'disabled': isDisabled }, {'undroppable': !droppableColumn(kanbanColumn.status.id)}, {'collapsed': isColumnCollapsed(kanbanColumn.status.id)}]"
          class="kanban-column"
        >
          <!-- COLLAPSED COLUMN -->
          <div
            v-if="isColumnCollapsed(kanbanColumn.status.id)"
            class="kanban-column__collapsed"
            title="Expand column"
            @click="toggleColumnCollapsed(kanbanColumn.status.id)"
          >
            <i class="ddi ddi-caret-right" />
            <span
              :class="'bg-' + kanbanColumn.status.color + '-flat'"
              class="kanban-column__collapsed__label"
            >
              {{ kanbanColumn.status.name }} ({{ getMatchCount(kanbanColumn) }})
            </span>
          </div>
          <div v-show="!isColumnCollapsed(kanbanColumn.status.id)" class="kanban-column__header">
            <status-chicklet
              :status="kanbanColumn.status"
              :count="getMatchCount(kanbanColumn)"
              type="flat"
              size="medium"
            />
            <span
              v-if="!isMobile"
              class="kanban-column__header__collapse-toggle"
              title="Collapse column"
              @click="toggleColumnCollapsed(kanbanColumn.status.id)"
            >
              <i class="ddi ddi-caret-left" />
            </span>
          </div>
          <div v-show="!isColumnCollapsed(kanbanColumn.status.id)" :class="{'first-holder' : index === 0 && !$screen.isMobile($mq)}" class="kanban-column__card-holder">
            <draggable
              :animation="180"
              :scroll-sensitivity="100"
              :scroll-speed="isMobile ? 0 : 20"
              :force-fallback="true"
              :disabled="isDisabled"
              :delay="isMobile ? 300 : 0"
              :delay-on-touch-only="true"
              :list="kanbanColumn.tasks"
              :move="checkMove"
              :group="itemGroupName(kanbanColumn.status.id)"
              ghost-class="ghost-card"
              drag-class="drag-card"
              tag="ul"
              item-key="taskID"
              @change="(val: any) => handleMovedItem(val, kanbanColumn.status.id)"
              @sort="(val: any) => handleMovedItem(val, kanbanColumn.status.id)"
              @choose="(evt: any) => 
                onChoosing(kanbanColumn.status.id, evt)"
              @start="onDragStart"
              @unchoose="onUnchoosing()"
            >
              <template #item="{ element: task }">
                <li
                  :id="getCardID(task.taskID)"
                  :class="[{'no-match' : !task.filterMatch}, {'newly-added' : isNewTask(task.taskID)}]"
                  class="kanban-card"
                  @click="handleCardClick(task)"
                  @contextmenu.prevent
                >
                  <h4>#{{ task.refNumber }}: {{ task.title }}</h4>
                  <h4 v-if="task.customerID">
                    <div v-if="getCustomer(task.customerID)">
                      <avatar :size="14" :person="getCustomer(task.customerID)" show-name-on-hover />
                      <span>
                        {{ getCustomer(task.customerID).name }}
                        <span
                          v-if="getCustomer(task.customerID).name !== getCustomer(task.customerID).email"
                          class="customer-email"
                        >&lt;{{ getCustomer(task.customerID).email }}&gt;</span>
                      </span>
                    </div>
                  </h4>
                  <div v-if="task.tags && task.tags.length > 0" class="task-tags-wrapper">
                    <div class="task-tags-container">
                      <span
                        v-for="tag in task.tags"
                        :key="tag"
                        class="task-tag"
                      >
                        {{ tag }}
                      </span>
                    </div>
                  </div>
                  <custom-field-value-pills
                    v-if="customFields.length > 0"
                    :fields="customFields"
                    :values="task.listCustomFieldValues"
                    class="kanban-card__custom-fields"
                  />
                  <div>
                    <due-date-chicklet :due-date="task.dueDate" />
                    <priority-chicklet :priority="getPriorityForItem(task)" />
                    <avatar :person="getAssigneeForItem(task)" :size="20" />
                    <span v-if="task.totalChecklistItemsCount > task.completedChecklistItemsCount" class="kanban-column__card-holder__counts">
                      <span class="ddi ddi-everything" />
                      <span>({{ task.completedChecklistItemsCount }}/{{ task.totalChecklistItemsCount }})</span>
                    </span>
                    <div>{{task.lastUpdated}}</div>
                  </div>
                </li>
              </template>
            </draggable>
          </div>
          <div v-if="index===0 && !$screen.isMobile($mq) && !isColumnCollapsed(kanbanColumn.status.id)" class="kanban-column__footer">
            <new-conversation-button
              v-if="isMailbox"
              :mailbox-id="entityId"
              size="medium"
              class="marker--kanban-column--new-convo-button"
            />
            <new-task-button
              v-else
              size="medium"
              disable-click
              class="marker--kanban-column--new-task-button"
              @click="$emit('new-task-clicked')"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import Draggable from 'vuedraggable'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import LocalStore from '@/helpers/localstore'

/* Data types */
import {
  IHomeKanbanColumn,
  ITaskForKanbanWithManualOrder,
} from '@/types/data-objects/items'
import { SortOptionValues } from '@/types'

/* API */
import {
  ICustomField,
  IKanbanColumnOrdering,
  IPersonBase,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IStatusForSelection,
  ITaskForKanban,
  IWorkflowDetailStatusTransition,
} from '@/api/types/response.types'

/* Matches the stylesheet fallback for the columns' top offset on desktop */
const DEFAULT_COLUMNS_TOP = 195
/* Space kept below the columns, mirrored from the original calc(100% - 220px) */
const COLUMNS_BOTTOM_GUTTER = 25

export default defineComponent({
  name: 'KanbanBoard',

  components: {
    Draggable,
  },

  props: {
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: Number,
      required: true,
    },
    isMailbox: {
      type: Boolean,
      default: false,
    },
    tasks: {
      type: Array as () => ITaskForKanbanWithManualOrder[],
      default: null,
    },
    columnOrderings: {
      type: Array as () => IKanbanColumnOrdering[],
      default: null,
    },
    statuses: {
      type: Array as () => IStatusForSelection[],
      default: null,
    },
    members: {
      type: Array as () => IPersonBase[],
      default: null,
    },
    customers: {
      type: Array as () => IPersonWithEmail[],
      default: () => [] as IPersonWithEmail[],
    },
    priorities: {
      type: Array as () => IPriorityLevelForSelection[],
      default: null,
    },
    workflowStatusTransitions: {
      type: Array as () => IWorkflowDetailStatusTransition[],
      default: null,
    },
    selectedSort: {
      type: String,
      default: SortOptionValues.UPDATED_RECENT,
    },
    /* Custom fields flagged to show on listings; matched against each card's values */
    customFields: {
      type: Array as () => ICustomField[],
      default: (): ICustomField[] => [],
    },
  },

  emits: [
    'dragging-completed',
    'dragging-started',
    'moved-task',
    'new-task-clicked',
  ],

  data() {
    return {
      isDragging: false,
      RouteAssistant,
      curSelectedStatusID: null as any,
      kanbanColumns: [] as Array<IHomeKanbanColumn>,
      collapsedStatusIDs: [] as Array<number>,
      newlyAddedTaskIDs: [] as Array<number>,
      activeColumnIndex: 0,
      scrollRafPending: false,
      choosingEl: null as HTMLElement | null,
      suppressNextClick: false,
      dragSourceColumn: null as HTMLElement | null,
      /* Desktop: where the absolutely positioned columns start, measured from the board's place in the page flow */
      columnsTop: DEFAULT_COLUMNS_TOP,
      // Typed loosely: the TypeScript lib in use predates ResizeObserver
      headerResizeObserver: null as any,
      snapCooldown: false,
      dragMoveHandler: null as ((e: TouchEvent) => void) | null,
    }
  },

  computed: {
    /*
     * The columns are absolutely positioned on desktop, so a hard-coded top offset overlaps the filters panel
     * once the filter chips wrap to a second row. Measuring the board's own position in the flow keeps the
     * columns just below whatever height the header ends up with. Mobile lays the columns out in the flow.
     */
    columnsStyle(): Record<string, string> {
      if (this.isMobile) {
        return {}
      }
      return {
        top: `${this.columnsTop}px`,
        maxHeight: `calc(100% - ${this.columnsTop + COLUMNS_BOTTOM_GUTTER}px)`,
      }
    },
    isLoading(): boolean {
      return this.kanbanColumns.length === 0
    },
    userMap(): Map<number, IPersonWithEmail> {
      const map = new Map<number, IPersonWithEmail>()
      this.customers.forEach(c => {
        map.set(c.id, c)
      })
      return map
    },
  },

  watch: {
    tasks: {
      deep: 1,
      immediate: true,
      handler() {
        this.refreshColumns()
      },
    },
    statuses: {
      deep: 1,
      immediate: true,
      handler() {
        this.refreshColumns()
      },
    },
    selectedSort: {
      handler() {
        this.refreshColumns()
      },
    },
  },

  created() {
    this.collapsedStatusIDs = LocalStore.getKanbanCollapsedColumns(
      this.entityId,
    )
  },

  mounted() {
    this.updateColumnsTop()
    this.observeHeaderHeight()
    window.addEventListener('resize', this.updateColumnsTop)

    this.$watch(
      'isLoading',
      (val: boolean) => {
        if (!val) {
          this.$nextTick(() => {
            this.attachScrollListener()
            this.updateMobileHeight()
            // Re-measure after layout settles (e.g. on page refresh)
            setTimeout(() => this.updateMobileHeight(), 300)
            if (this.isMobile) {
              this.$bodyScroll.disable()
              const saved = sessionStorage.getItem(
                `kanban-col-${this.entityId}`,
              )
              if (saved) {
                const parsed = parseInt(saved, 10)
                if (Number.isFinite(parsed) && parsed >= 0) {
                  const index = Math.min(parsed, this.kanbanColumns.length - 1)
                  this.activeColumnIndex = index
                  this.scrollToColumn(index)
                }
              }
            }
          })
        }
      },
      { immediate: true },
    )
  },

  beforeUnmount() {
    if (this.isMobile) {
      this.$bodyScroll.enable()
    }
    this.detachScrollListener()
    window.removeEventListener('resize', this.updateColumnsTop)
    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect()
      this.headerResizeObserver = null
    }
  },

  methods: {
    /* Distance from the positioned ancestor's top to where the board sits in the flow */
    updateColumnsTop() {
      const root = this.$el as HTMLElement
      const offsetParent = root ? (root.offsetParent as HTMLElement) : null
      if (!root || !offsetParent) {
        return
      }
      const top = Math.round(
        root.getBoundingClientRect().top -
          offsetParent.getBoundingClientRect().top,
      )
      if (top > 0) {
        this.columnsTop = top
      }
    },

    /* The filters panel above the board grows when its chips wrap, so re-measure whenever the header resizes */
    observeHeaderHeight() {
      const Observer = (window as any).ResizeObserver
      if (!Observer) {
        return
      }
      const listing = (this.$el as HTMLElement).closest('.items-listing')
      const header = listing
        ? listing.querySelector('.items-listing__main-header')
        : null
      if (!header) {
        return
      }
      this.headerResizeObserver = new Observer(() => {
        this.updateColumnsTop()
      })
      this.headerResizeObserver.observe(header)
    },

    updateMobileHeight() {
      if (!this.isMobile) {
        return
      }
      const container = this.$refs.columnsContainer as HTMLElement
      if (!container) {
        return
      }
      const rect = container.getBoundingClientRect()
      const mobileMenuHeight = 55
      const jsHeight = window.innerHeight - rect.top - mobileMenuHeight
      const cssHeight = container.offsetHeight
      if (jsHeight < cssHeight) {
        container.style.height = `${jsHeight}px`
      }
    },
    attachScrollListener() {
      if (!this.isMobile) {
        return
      }
      this.detachScrollListener()
      const container = this.$refs.columnsContainer as HTMLElement
      if (container) {
        container.addEventListener('scroll', this.onColumnsScroll, {
          passive: true,
        })
      }
    },
    detachScrollListener() {
      const container = this.$refs.columnsContainer as HTMLElement
      if (container) {
        container.removeEventListener('scroll', this.onColumnsScroll)
      }
    },
    onColumnsScroll() {
      if (!this.isMobile || this.scrollRafPending) {
        return
      }
      this.scrollRafPending = true
      requestAnimationFrame(() => {
        this.scrollRafPending = false
        const container = this.$refs.columnsContainer as HTMLElement
        if (!container) {
          return
        }
        const columns = container.querySelectorAll('.kanban-column')
        if (columns.length === 0) {
          return
        }
        const isPhone = window.innerWidth < 768
        let closestIndex = 0
        if (isPhone) {
          // Phone: find column whose center is closest to the viewport center
          const containerCenter =
            container.scrollLeft + container.clientWidth / 2
          let closestDistance = Infinity
          columns.forEach((col, index) => {
            const el = col as HTMLElement
            const colCenter = el.offsetLeft + el.offsetWidth / 2
            const distance = Math.abs(containerCenter - colCenter)
            if (distance < closestDistance) {
              closestDistance = distance
              closestIndex = index
            }
          })
        } else {
          // Tablet: track the left-most visible column (left edge closest to scrollLeft)
          let closestDistance = Infinity
          columns.forEach((col, index) => {
            const el = col as HTMLElement
            const dist = Math.abs(el.offsetLeft - container.scrollLeft)
            if (dist < closestDistance) {
              closestDistance = dist
              closestIndex = index
            }
          })
        }
        if (closestIndex !== this.activeColumnIndex) {
          this.activeColumnIndex = closestIndex
          sessionStorage.setItem(
            `kanban-col-${this.entityId}`,
            String(closestIndex),
          )
        }
      })
    },
    scrollToColumn(
      index: number,
      smooth = false,
      direction: 'auto' | 'left' | 'right' = 'auto',
    ) {
      const container = this.$refs.columnsContainer as HTMLElement
      if (!container) {
        return
      }
      const columns = container.querySelectorAll('.kanban-column')
      if (index >= columns.length) {
        return
      }
      const target = columns[index] as HTMLElement
      const isPhone = window.innerWidth < 768
      const style = getComputedStyle(container)
      const scrollPaddingLeft =
        parseFloat((style as any).scrollPaddingLeft) || 0
      const scrollPaddingRight =
        parseFloat((style as any).scrollPaddingRight) || 0
      let targetLeft: number
      if (direction === 'right') {
        // Right-align: column's right edge at the snap-port's right edge
        targetLeft =
          target.offsetLeft +
          target.offsetWidth -
          (container.clientWidth - scrollPaddingRight)
      } else if (
        direction === 'left' ||
        (direction === 'auto' && this.isMobile && !isPhone)
      ) {
        // Left-align: column's left edge at the snap-port's left edge (accounts for scroll-padding)
        targetLeft = target.offsetLeft - scrollPaddingLeft
      } else {
        // Center-align: phone default
        targetLeft =
          target.offsetLeft - (container.clientWidth - target.offsetWidth) / 2
      }
      if (!smooth) {
        container.scrollLeft = targetLeft
        return
      }
      // Use rAF-based smooth scroll instead of behavior:'smooth' which
      // locks iOS Safari's touch scroll after the animation completes.
      // Disable snap for the duration so it doesn't fight the animation.
      ;(container.style as any).scrollSnapType = 'none'
      const startLeft = container.scrollLeft
      const distance = targetLeft - startLeft
      const duration = 500
      const startTime = performance.now()
      const step = (now: number) => {
        const elapsed = now - startTime
        const progress = Math.min(elapsed / duration, 1)
        const ease =
          progress < 0.5
            ? 2 * progress * progress
            : -1 + (4 - 2 * progress) * progress
        container.scrollLeft = startLeft + distance * ease
        if (progress < 1) {
          requestAnimationFrame(step)
        } else if (!this.isDragging) {
          ;(container.style as any).scrollSnapType = ''
        }
      }
      requestAnimationFrame(step)
    },
    /* Collapsed columns are a desktop-only affordance — mobile relies on
       snap-scrolling between uniform-width columns. */
    isColumnCollapsed(statusID: number): boolean {
      return !this.isMobile && this.collapsedStatusIDs.includes(statusID)
    },
    toggleColumnCollapsed(statusID: number) {
      if (this.collapsedStatusIDs.includes(statusID)) {
        this.collapsedStatusIDs = this.collapsedStatusIDs.filter(
          id => id !== statusID,
        )
      } else {
        this.collapsedStatusIDs = [...this.collapsedStatusIDs, statusID]
      }
      LocalStore.setKanbanCollapsedColumns(
        this.entityId,
        this.collapsedStatusIDs,
      )
    },
    /* Renames the kanban columns such that 'droppableGroup' are all columns that the current
       card can drop to (via the curSelectedStatusID assignment on "choose") and 'noDropGroup' are
       all columns that it can't drop into, by inspecting the workflow status transitions. */
    itemGroupName(statusID: number): string {
      return this.droppableColumn(statusID) ? 'dropGroup' : 'noDropGroup'
    },
    droppableColumn(statusID: number): boolean {
      if (this.curSelectedStatusID === null) {
        return true
      }
      if (
        this.workflowStatusTransitions.find(
          w =>
            this.curSelectedStatusID === statusID ||
            (w.statusIDFrom === this.curSelectedStatusID &&
              w.statusIDTo === statusID),
        )
      ) {
        return true
      }
      return false
    },
    onDragStart() {
      // Sortable sizes its fallback clone via getBoundingClientRect(), which includes
      // the rotation from pre-drag and returns an inflated height. Capture the true
      // layout height first, then correct the clone after removing pre-drag.
      const originalHeight = this.choosingEl ? this.choosingEl.offsetHeight : 0
      document.querySelectorAll('.pre-drag').forEach(el => {
        el.classList.remove('pre-drag')
      })
      this.choosingEl = null
      if (originalHeight) {
        const fallback = document.querySelector(
          '.sortable-fallback',
        ) as HTMLElement
        if (fallback) {
          fallback.style.height = `${originalHeight}px`
        }
      }
      if (this.isMobile) {
        this.dragMoveHandler = (e: TouchEvent) => {
          if (this.snapCooldown || !e.touches.length) {
            return
          }
          const x = e.touches[0].clientX
          const w = window.innerWidth
          const isPhone = w < 768
          // On tablet, activeColumnIndex is the left-visible column.
          // Cap at length - 2 so the right column is always visible.
          const maxIndex = isPhone
            ? this.kanbanColumns.length - 1
            : Math.max(0, this.kanbanColumns.length - 2)
          if (x < w * 0.25 && this.activeColumnIndex > 0) {
            this.snapCooldown = true
            this.activeColumnIndex--
            sessionStorage.setItem(
              `kanban-col-${this.entityId}`,
              String(this.activeColumnIndex),
            )
            this.scrollToColumn(this.activeColumnIndex, true)
            setTimeout(() => {
              this.snapCooldown = false
            }, 600)
          } else if (x > w * 0.75 && this.activeColumnIndex < maxIndex) {
            this.snapCooldown = true
            this.activeColumnIndex++
            sessionStorage.setItem(
              `kanban-col-${this.entityId}`,
              String(this.activeColumnIndex),
            )
            this.scrollToColumn(this.activeColumnIndex, true)
            setTimeout(() => {
              this.snapCooldown = false
            }, 600)
          }
        }
        document.addEventListener('touchmove', this.dragMoveHandler, {
          passive: true,
        })
      }
    },
    onChoosing(statusID: number, evt: any) {
      this.isDragging = true
      this.curSelectedStatusID = statusID
      if (this.isMobile) {
        this.suppressNextClick = true
        if (navigator.vibrate) {
          navigator.vibrate(10)
        }
        if (evt && evt.item) {
          this.choosingEl = evt.item as HTMLElement
          this.choosingEl.classList.add('pre-drag')
          const col = this.choosingEl.closest('.kanban-column') as HTMLElement
          if (col) {
            col.style.position = 'relative'
            col.style.zIndex = '100'
            this.dragSourceColumn = col
          }
        }
        const container = this.$refs.columnsContainer as HTMLElement
        if (container) {
          ;(container.style as any).scrollSnapType = 'none'
        }
      }
      this.$emit('dragging-started')
    },
    onUnchoosing() {
      this.isDragging = false
      this.curSelectedStatusID = null
      if (this.choosingEl) {
        this.choosingEl.classList.remove('pre-drag')
        this.choosingEl = null
      }
      if (this.dragSourceColumn) {
        this.dragSourceColumn.style.position = ''
        this.dragSourceColumn.style.zIndex = ''
        this.dragSourceColumn = null
      }
      if (this.dragMoveHandler) {
        document.removeEventListener('touchmove', this.dragMoveHandler)
        this.dragMoveHandler = null
      }
      this.snapCooldown = false
      if (this.isMobile) {
        const container = this.$refs.columnsContainer as HTMLElement
        if (container) {
          ;(container.style as any).scrollSnapType = ''
        }
        // Reset after the synthesized click from touchend has had a chance to fire.
        setTimeout(() => {
          this.suppressNextClick = false
        }, 50)
      }
      this.$emit('dragging-completed')
    },
    handleMovedItem(val: any, statusID: number) {
      const tasksForStatus = this.kanbanColumns.find(
        c => c.status.id === statusID,
      ).tasks

      /* Mark all tasks as manually ordered */
      tasksForStatus.forEach(t => (t.isManuallyOrdered = true))
      const idOrder = tasksForStatus.map(t => t.taskID)

      const taskID = val.added
        ? val.added.element.taskID
        : val.moved
          ? val.moved.element.taskID
          : null

      if (taskID) {
        this.$emit('moved-task', taskID, statusID, idOrder)
      }

      /* On mobile, scroll to the column the task was dropped into */
      if (this.isMobile && val.added) {
        const columnIndex = this.kanbanColumns.findIndex(
          c => c.status.id === statusID,
        )
        if (columnIndex >= 0) {
          const isPhone = window.innerWidth < 768
          if (isPhone) {
            // Phone: center the dropped-into column
            this.$nextTick(() => {
              this.scrollToColumn(columnIndex, true)
            })
          } else {
            // Tablet: don't scroll if the column is already one of the 2 visible;
            // left-align if it's off-screen to the left, right-align if off-screen to the right.
            const rightVisible = this.activeColumnIndex + 1
            if (
              columnIndex === this.activeColumnIndex ||
              columnIndex === rightVisible
            ) {
              // Already visible — no scroll needed
            } else if (columnIndex < this.activeColumnIndex) {
              this.activeColumnIndex = columnIndex
              sessionStorage.setItem(
                `kanban-col-${this.entityId}`,
                String(columnIndex),
              )
              this.$nextTick(() => {
                this.scrollToColumn(columnIndex, true, 'left')
              })
            } else {
              // Off-screen to the right — right-align so it becomes the right visible column
              const newLeft = columnIndex - 1
              this.activeColumnIndex = newLeft
              sessionStorage.setItem(
                `kanban-col-${this.entityId}`,
                String(newLeft),
              )
              this.$nextTick(() => {
                this.scrollToColumn(columnIndex, true, 'right')
              })
            }
          }
        }
      }
    },
    refreshColumns() {
      this.kanbanColumns = new Array<IHomeKanbanColumn>()
      this.statuses.forEach(s => {
        this.kanbanColumns.push({
          status: s,
          tasks: this.orderTasksInColumn(s.id),
        })
      })
    },
    hasTasks(statusID: number): boolean {
      if (this.tasks.find(i => i.statusID === statusID)) {
        return true
      }
      return false
    },
    compareTasksBySelectedSort(
      a: ITaskForKanbanWithManualOrder,
      b: ITaskForKanbanWithManualOrder,
    ): number {
      switch (this.selectedSort) {
        case SortOptionValues.UPDATED_OLDEST:
          return a.lastUpdatedEpochTime - b.lastUpdatedEpochTime
        case SortOptionValues.CREATED_RECENT:
          // refNumber is monotonically increasing per project, so it's a faithful proxy for creation order.
          return b.refNumber - a.refNumber
        case SortOptionValues.CREATED_OLDEST:
          return a.refNumber - b.refNumber
        case SortOptionValues.DUE_DATE: {
          // Tasks without a due date sort to the bottom.
          if (!a.dueDate && !b.dueDate) {
            return 0
          }
          if (!a.dueDate) {
            return 1
          }
          if (!b.dueDate) {
            return -1
          }
          return a.dueDate.localeCompare(b.dueDate)
        }
        case SortOptionValues.TITLE:
          return (a.title || '').localeCompare(b.title || '', undefined, {
            sensitivity: 'base',
          })
        case SortOptionValues.ASSIGNEE: {
          const nameFor = (id?: number) => {
            if (!id || !this.members) {
              return ''
            }
            const member = this.members.find(m => m.id === id)
            return member ? member.name : ''
          }
          const aName = nameFor(a.assigneeID)
          const bName = nameFor(b.assigneeID)
          // Unassigned (empty name) sorts to the bottom.
          if (!aName && !bName) {
            return 0
          }
          if (!aName) {
            return 1
          }
          if (!bName) {
            return -1
          }
          return aName.localeCompare(bName, undefined, { sensitivity: 'base' })
        }
        case SortOptionValues.PRIORITY:
          return (a.priorityLevelID || 0) - (b.priorityLevelID || 0)
        case SortOptionValues.STATUS:
          // Columns already partition by status, so this is a no-op within a column.
          return 0
        case SortOptionValues.UPDATED_RECENT:
        default:
          return b.lastUpdatedEpochTime - a.lastUpdatedEpochTime
      }
    },
    orderTasksInColumn(statusID: number): ITaskForKanbanWithManualOrder[] {
      const tasksInColumn = this.tasks
        .filter(i => i.statusID === statusID)
        .map(t => ({ ...t, isManuallyOrdered: false }))

      // "Custom" sort: respect the saved drag/drop ordering; unsaved tasks append at the bottom.
      if (this.selectedSort === SortOptionValues.STATUS) {
        const columnOrdering = this.columnOrderings.find(
          c => c.statusID === statusID,
        )
        if (!columnOrdering || columnOrdering.listItemIDs.length === 0) {
          return tasksInColumn
        }
        const result: ITaskForKanbanWithManualOrder[] = []
        columnOrdering.listItemIDs.forEach(id => {
          const task = tasksInColumn.find(t => t.taskID === id)
          if (task) {
            result.push(task)
          }
        })
        const extras = tasksInColumn.filter(
          t => !columnOrdering.listItemIDs.includes(t.taskID),
        )
        return [...result, ...extras]
      }

      return tasksInColumn.sort((a, b) => this.compareTasksBySelectedSort(a, b))
    },
    getPriorityForItem(task: ITaskForKanbanWithManualOrder) {
      return this.priorities.find(p => p.id === task.priorityLevelID)
    },
    getAssigneeForItem(task: ITaskForKanbanWithManualOrder) {
      if (task.assigneeID) {
        return this.members.find(p => p.id === task.assigneeID)
      }
      return null
    },
    getCustomer(customerID: number): IPersonWithEmail | null {
      return this.userMap.get(customerID) || null
    },
    getMatchCount(kanbanColumn: IHomeKanbanColumn) {
      return kanbanColumn.tasks.filter(t => t.filterMatch).length
    },
    getCardID(taskID: number): string {
      return `kanban-board-task-${taskID}`
    },
    isNewTask(taskID: number): boolean {
      return this.newlyAddedTaskIDs.includes(taskID)
    },
    async scrollToNewTask(taskID: number) {
      this.newlyAddedTaskIDs.push(taskID)
      await this.$nextTick()
      if (this.isMobile) {
        const container = this.$refs.columnsContainer as HTMLElement
        if (container) {
          container.scrollTo({ left: 0, behavior: 'smooth' })
        }
        await this.$nextTick()
      }
      const card = document.getElementById(this.getCardID(taskID))
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    },
    clearNewTasks() {
      this.newlyAddedTaskIDs = []
    },
    handleCardClick(task: ITaskForKanbanWithManualOrder) {
      if (this.suppressNextClick) {
        return
      }
      if (this.isMailbox) {
        RouteAssistant.goToConversationFromKanbanBoard(
          this.$router,
          this.$route.params.accountId,
          this.entityId,
          task.taskID,
          task.refNumber,
        )
      } else {
        RouteAssistant.goToTaskFromKanbanBoard(
          this.$router,
          this.$route.params.accountId,
          this.entityId,
          task.taskID,
          this.$permissions,
          task.refNumber,
        )
      }
    },
    checkMove(evt: any): boolean {
      return (
        this.$permissions.isNotGuest ||
        evt.draggedContext.element.assigneeID ===
          this.$permissions.loggedInUser.id
      )
    },
  },
})
</script>

<style lang="scss">
@keyframes newTaskCardFadeIn {
  0% {
    background-color: var(--color-background-blue-15);
  }
  100% {
    background-color: var(--color-background-header);
  }
}

// Global scope so styles apply when Sortable moves the ghost to document.body
.kanban-card {
  @include disable-select;
  -webkit-touch-callout: none;
  list-style-type: none;
  background-color: var(--color-background-header);
  padding: 10px;
  margin-bottom: 8px;
  border-radius: 8px;
  cursor: pointer;
  border: dashed 1px $color--gray-30;
  border-bottom: dashed 1px $color--gray-45;
  border-right: dashed 1px $color--gray-45;

  &.no-match {
    display: none;
  }

  &.newly-added {
    opacity: 1;
    animation: newTaskCardFadeIn ease 1.2s;
    border-style: dashed;
  }

  > h4 {
    @include lettering--p;
    margin-bottom: 0;
  }

  > div {
    margin-top: 6px;
    display: flex;
    align-items: flex-end;

    > *:not(:last-child) {
      margin-right: 4px;
    }
    > *:last-child {
      margin-left: auto;
      @include lettering--p-xs;
      @include single-line-ellipsis;
    }
  }

  /* The pills block is a div too, so undo the footer-row rules above that would push the last pill to the right */
  > .kanban-card__custom-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 4px 0 6px;

    > *:last-child {
      margin-left: 0;
      font-size: 12px;
      line-height: 1.4;
      color: var(--color-text-secondary);
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
    }
  }
}

.drag-card {
  cursor: grab !important;
  transform: rotate(-3deg);
  z-index: 2000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
  opacity: 0.96;
  margin: 0 !important;
}

.kanban-board {
  .task-tags-wrapper {
    margin: 4px 0 8px 0;
    display: block !important;
    width: 100%;
    text-align: left !important;
  }

  .task-tags-container {
    display: block;
    text-align: left;
  }

  .task-tag {
    display: inline-block;
    padding: 4px 6px;
    margin-right: 4px;
    margin-bottom: 2px;
    font-size: 12px;
    white-space: nowrap;
    line-height: 1.2;
    vertical-align: top;
    background: var(--color-background-secondary);
    color: var(--color-text-secondary);
    border-radius: 20px;
    border-color: var(--color-text-third);
    border-width: 1px;
    border-style: solid;
  }

  .task-tag:last-child {
    margin-right: 0;
  }

  &__column-indicators {
    display: none;

    @include media-breakpoint-down(md) {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 6px;
      padding: 8px 0;
      background-color: var(--color-background-third);
      position: sticky;
      top: 0;
      z-index: 1;
    }

    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: var(--color-text-third);
      opacity: 0.35;
      transition: opacity 0.2s ease;

      &.active {
        opacity: 1;
        background-color: $color--blue;
      }
    }
  }

  &__columns {
    width: 100%;
    position: absolute;
    right: 0;
    top: 195px;
    max-height: calc(100% - 220px);
    display: flex;
    width: 100%;
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 20px;
    background-color: var(--color-background-third);

    &.mobile {
      @include media-breakpoint-down(md) {
        position: relative;
        top: auto;
        max-height: none;
        height: calc(100vh - 260px);
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      // Phone: 7.5vw side padding creates a peek of adjacent columns
      @include media-breakpoint-down(sm) {
        padding: 12px 7.5vw;
      }

      // Tablet: narrow padding + scroll-padding to inset the snap port,
      // giving a peek of adjacent columns on both sides
      @include media-breakpoint-between(sm, md) {
        padding: 12px 0;
        scroll-padding: 0 20px;
      }
    }

    /* Grab cursor overrides */
    &.dragging {
      cursor: grab;

      li {
        cursor: grab !important;
      }
    }

    &__loading {
      margin-top: 20px;
    }

    .kanban-column {
      min-width: 280px;
      flex: 1;

      @include media-breakpoint-down(sm) {
        scroll-snap-align: center;
        min-width: 85vw;
        max-width: 85vw;
        flex: 0 0 85vw;
      }

      @include media-breakpoint-between(sm, md) {
        scroll-snap-align: start;
        min-width: calc(50% - 25px);
        max-width: calc(50% - 25px);
        flex: 0 0 calc(50% - 25px);
      }

      &.disabled {
        opacity: 0.8;
      }

      &.undroppable {
        > * {
          opacity: 0.1;
        }
      }

      &:not(:last-child) {
        margin-right: 10px;
      }

      border-bottom: var(--border-solid-medium);
      border-right: var(--border-bottom);
      background-color: var(--color-background-secondary);
      border-radius: 8px;
      padding: 8px 0;

      &__card-holder {
        margin-top: 10px;
        padding: 10px;
        padding-top: 0;
        height: calc(100% - 42px);
        overflow-x: hidden;
        overflow-y: auto;

        &__counts {
          font-size: 13px;
          color: var(--color-text-third);
        }

        &.first-holder {
          height: calc(100% - 80px);
        }

        ul {
          margin: 0;
          padding: 0;
          height: 100%;
        }
      }

      &__header {
        padding: 0 10px;
        display: flex;
        align-items: center;

        .status-chicklet {
          flex: 1;
          min-width: 0;
        }

        &__collapse-toggle {
          cursor: pointer;
          margin-left: 6px;
          padding: 2px 4px;
          color: var(--color-text-third);
          font-size: 12px;

          &:hover {
            color: var(--color-text-secondary);
          }
        }
      }

      &.collapsed {
        min-width: 36px;
        max-width: 36px;
        flex: 0 0 36px;
      }

      &__collapsed {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
        overflow: hidden;
        padding-top: 8px;

        .ddi {
          color: var(--color-text-third);
          font-size: 12px;
        }

        &:hover .ddi {
          color: var(--color-text-secondary);
        }

        /* Vertical pill that mirrors the status chicklet (color comes
           from the same bg-<color>-flat classes the chicklet uses) */
        &__label {
          flex: 1;
          align-self: stretch;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 18px 4px 0 4px;
          writing-mode: vertical-rl;
          text-transform: uppercase;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.4px;
          padding: 10px 0;
          border-radius: 6px;
          white-space: nowrap;
          overflow: hidden;
        }
      }

      &__footer {
        padding: 9px 0 0 10px;
        border-top: $border--solid-light;
      }

      .pre-drag {
        transform: rotate(-3deg) !important;
        box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25) !important;
        opacity: 0.96 !important;
        position: relative;
        z-index: 10;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
      }

      .ghost-card {
        cursor: grab !important;
        opacity: 0.5 !important;
        > * {
          visibility: hidden;
        }
      }
    }
  }
}
</style>
